import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useListaFiltrada } from "@/lib/filtros";
import { type FiltrosPuntos, filtrarPuntos } from "@/lib/filtros/puntos";
import { fuentesQueryKey } from "@/lib/fuentes";

/**
 * CandadoDePunto es uno de los candados que cubren AHORA el punto (#248), con el LOTO al
 * que pertenece su bloqueo. Varios candados pueden cubrir el mismo punto (pinzas/hasps,
 * #244), de ahí que sea una lista.
 */
export type CandadoDePunto = {
  id_bloqueo: number;
  id_candado: number;
  codigo_candado: string;
  id_loto: number;
  numero_loto: string;
};

/**
 * Punto es un punto de aislamiento con el contexto de su fuente y equipo
 * (GET /api/v1/puntos). Se identifica por su `nombre` en campo, «Interruptor general»
 * (#246). `bloqueado` indica si algún candado sigue cubriéndolo (#243) y
 * `candados` dice CUÁLES y desde qué LOTO (#248, siempre presente: `[]` si ninguno). La UI
 * resalta en rojo y ordena arriba los puntos con `bloqueado=true`.
 */
export type Punto = {
  id: number;
  id_fuente: number;
  codigo_fuente: string;
  nombre_fuente: string;
  id_equipo: number;
  codigo_equipo: string;
  nombre_equipo: string;
  nombre: string;
  descripcion: string;
  activo: boolean;
  bloqueado: boolean;
  candados: CandadoDePunto[];
  version: number;
  updated_at: string;
};

/**
 * PuntoInput es el payload de alta/edición (POST/PUT /api/v1/puntos). En el ALTA
 * `id_equipo` es obligatorio e `id_fuente` opcional: si se omite, el servidor usa la
 * primera fuente del equipo o auto-crea una por defecto. En la EDICIÓN solo importan
 * `nombre`, `descripcion` y `activo` (la fuente padre es inmutable).
 */
export type PuntoInput = {
  id_equipo: number;
  id_fuente: number | null;
  nombre: string;
  descripcion: string;
  activo: boolean;
};

/**
 * puntosQueryKey namespacea la caché de puntos POR obra: `["obra", obraId, "puntos"]`
 * (F7 #158). Aísla en la caché de Query los puntos de una obra de los de otra.
 */
export function puntosQueryKey(obraId: number) {
  return ["obra", obraId, "puntos"] as const;
}

/**
 * puntosQueryOptions lista los puntos vivos con el contexto de su fuente/equipo de la obra
 * `obraId`. Se refresca invalidando la clave tras cada mutación. Nota: crear un punto puede
 * auto-crear una fuente, así que también se invalida la caché de fuentes de la obra.
 */
export function puntosQueryOptions(obraId: number) {
  return queryOptions({
    queryKey: puntosQueryKey(obraId),
    queryFn: () => api.get<Punto[]>(`/obras/${obraId}/puntos`),
  });
}

/**
 * usePuntos lista los puntos de la obra, opcionalmente FILTRADOS (#247): con red filtra el
 * servidor; sin red, la función pura sobre la lista base cacheada.
 */
export function usePuntos(obraId: number, filtros: FiltrosPuntos = {}) {
  return useListaFiltrada<Punto>({
    claveBase: puntosQueryKey(obraId),
    url: `/obras/${obraId}/puntos`,
    filtros,
    filtrar: filtrarPuntos,
  });
}

export function useCreatePunto(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PuntoInput) => api.post<Punto>(`/obras/${obraId}/puntos`, input),
    onSuccess: () => {
      // El alta de un punto puede auto-crear una fuente por defecto: refrescar ambas.
      void queryClient.invalidateQueries({ queryKey: puntosQueryKey(obraId) });
      void queryClient.invalidateQueries({ queryKey: fuentesQueryKey(obraId) });
    },
  });
}

export function useUpdatePunto(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: PuntoInput }) =>
      api.put<Punto>(`/obras/${obraId}/puntos/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: puntosQueryKey(obraId) }),
  });
}

export function useDeletePunto(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<void>(`/obras/${obraId}/puntos/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: puntosQueryKey(obraId) }),
  });
}

/**
 * ordenarPuntos ordena los puntos poniendo ARRIBA los bloqueados (candado activo,
 * F3/#37) y, dentro de cada grupo, por equipo → fuente → nombre. Data-driven sobre el
 * campo `bloqueado`, de modo que el resaltado y el orden se activan solos cuando F3 lo
 * pueble; hoy todos son false y el orden es simplemente por equipo/fuente/nombre.
 */
export function ordenarPuntos(puntos: Punto[]): Punto[] {
  return [...puntos].sort((a, b) => {
    if (a.bloqueado !== b.bloqueado) {
      return a.bloqueado ? -1 : 1;
    }
    return (
      a.codigo_equipo.localeCompare(b.codigo_equipo) ||
      a.codigo_fuente.localeCompare(b.codigo_fuente) ||
      a.nombre.localeCompare(b.nombre)
    );
  });
}
